/**
 * Finds reports the agent created / edited / cloned (manage_reports, manage_custom_reports) in a message's
 * tool steps, so the chat can offer a "View report" button. Works on live Redux steps and saved history alike.
 */
export interface AgentReportCard {
  kind: 'report' | 'custom'
  id: string
  title: string
  typeLabel?: string
  /** search_type (e.g. 'customOrderBases') when the tool output carried it; drives the panel's default date range. */
  reportType?: string
  action: string
  /** Short facts for the card footer, e.g. ['Filtered', 'Shared with team', 'Daily email']. */
  details: string[]
}

// Only reports the agent made or changed, or ran on request. A `get` is the agent reading a report (often a
// template it is inspecting before proposing something), so it must not produce a card.
const SHOW_ACTIONS = new Set(['create', 'update', 'clone'])
const RUN_TOOL = 'run_saved_report'

const stripPrefix = (tool: string | undefined) => (tool || '').toLowerCase().replace(/^mcp__payhelm__/, '').replace(/^payhelm__/, '')

const parseJson = (raw: unknown): any => {
  if (raw && typeof raw === 'object') return raw
  if (typeof raw !== 'string') return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

const parseInput = (raw: unknown): Record<string, any> => {
  const v = parseJson(raw)
  return v && typeof v === 'object' ? v : {}
}

const INTERVAL_LABEL: Record<string, string> = { DAILY: 'Daily', WEEKLY: 'Weekly', MONTHLY: 'Monthly' }

/** Human-readable facts about the saved report, from the tool result metadata. */
function describeReport(kind: 'report' | 'custom', meta: Record<string, any>): string[] {
  const out: string[] = []
  if (kind === 'report') {
    const filter = meta.settings?.filter_config
    const trafficFilters = meta.settings?.params?.filters
    if (filter || (Array.isArray(trafficFilters) && trafficFilters.length)) out.push('Filtered')
    if (meta.visibility === 'My Team') out.push('Shared with team')
    const sch = meta.schedule || {}
    const interval = INTERVAL_LABEL[String(sch.interval || '').toUpperCase()]
    if (interval) out.push(`${interval} ${sch.target === 'GOOGLE_DRIVE' ? 'to Drive' : 'email'}`)
  } else {
    const filters = meta.settings?.filters
    if (filters && Object.keys(filters).length) out.push('Filtered')
    if (meta.visibility === 'Public') out.push('Shared with team')
    const email = INTERVAL_LABEL[String(meta.email?.interval || '').toUpperCase()]
    const drive = INTERVAL_LABEL[String(meta.google_drive?.interval || '').toUpperCase()]
    if (email) out.push(`${email} email`)
    if (drive) out.push(`${drive} to Drive`)
  }
  return out
}

export function extractAgentReportCards(steps: any[] | null | undefined): AgentReportCard[] {
  if (!Array.isArray(steps)) return []
  const byKey = new Map<string, AgentReportCard>()
  for (const step of steps) {
    if (!step || step.status !== 'completed') continue
    const tool = stripPrefix(step.tool)
    const kind = tool === 'manage_reports' || tool === 'run_saved_report' ? 'report' : tool === 'manage_custom_reports' ? 'custom' : null
    if (!kind) continue
    // run_saved_report has no action: a completed run always shows the report it ran.
    const input = parseInput(step.toolInput)
    const action = tool === RUN_TOOL ? 'get' : String(input.action || '').toLowerCase()
    if (tool !== RUN_TOOL && !SHOW_ACTIONS.has(action)) continue

    const out = parseJson(step.toolOutput)
    const first = out?.results?.[0]
    if (first?.id === 'error' || first?.metadata?.error) continue
    const meta = first?.metadata || {}
    // A disambiguation result (several reports share the name) is a question for the user, not a report.
    if (first?.id === 'needs_selection' || meta.needs_user_choice) continue
    let id: string | undefined =
      kind === 'report'
        ? meta.report_id || meta.saved_report_id
        : meta.custom_report_id != null ? String(meta.custom_report_id) : undefined
    // Truncated outputs (non-SDK loop slices to 500 chars): fall back to the id embedded in the result URL.
    if (!id && typeof step.toolOutput === 'string') {
      const m = step.toolOutput.match(kind === 'report' ? /\/reports\/([A-Za-z0-9]+)/ : /\/custom\/(\d+)/)
      if (m) id = m[1]
    }
    if (!id) continue
    byKey.set(`${kind}:${id}`, {
      kind,
      id,
      // Fall back to what the agent asked for when the output was truncated and carries no metadata.
      title:
        (kind === 'report' ? meta.title || meta.saved_report_title || input.title || input.report_name : meta.name || input.name || input.custom_report_name)
        || 'Report',
      typeLabel: kind === 'report' ? meta.report_type_label || meta.saved_report_type_label : 'Dashboard Report',
      reportType: kind === 'report' ? meta.report_type || meta.saved_report_type : undefined,
      action,
      details: describeReport(kind, meta),
    })
  }
  return [...byKey.values()]
}
